// Cliente de fetch das functions do painel admin (same-origin /api).
//
// Endpoints específicos do manager crescem por seção (Usuários, Professores,
// Grupos, etc.). Este arquivo concentra o transporte (call, ApiError) +
// tipos compartilhados de sessão.

export interface UserSession {
  id: string;
  email: string;
  name: string;
}

export interface ActiveContext {
  kind: "platform" | "company";
  companyId?: string;
  companyName?: string;
  role: string;
}

export interface MeResponse {
  user: UserSession;
  activeContext: ActiveContext | null;
  contexts: ActiveContext[];
}

// Tipos de Recado (manager-announcement).
export interface AnnouncementAudience {
  mode: "all" | "restricted";
  emails: string[];
  groupIds: string[];
}

export interface AdminFaqEntry {
  id: string;
  question: string;
  answer: string;
  category: string | null;
  position: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FaqConfig {
  isEnabled: boolean;
  updatedAt: number | null;
  updatedBy: string | null;
}

export interface AdminUserReport {
  id: string;
  kind: "bug" | "feature" | "bank_request" | "feedback" | "suporte";
  rating: number | null;
  title: string;
  description: string | null;
  status: "open" | "in_progress" | "resolved" | "wontfix";
  severity: "low" | "medium" | "high" | "critical" | null;
  context: Record<string, unknown> | null;
  reporterUserId: string;
  reporterName: string | null;
  reporterEmail: string | null;
  companyId: string | null;
  companyName: string | null;
  adminUserId: string | null;
  adminName: string | null;
  adminResponse: string | null;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AnnouncementRecord {
  enabled: boolean;
  title: string;
  text: string;
  tone: "info" | "warning" | "success" | "danger" | "neutral";
  audience: AnnouncementAudience;
  savedBy: string | null;
  savedAt: number | null;
  enabledAt: number | null;
}

// Tipos de Jogos (manager-games-config).
export interface GameRec {
  enabled: boolean;
  isNew: boolean;
  name: string;
  desc: string;
  tag: string;
  thumbUrl: string | null;
}

export interface GameCatalogEntry {
  id: string;
  path: string;
  defaultName: string;
  defaultDesc: string;
  defaultTag: string;
}

export interface GamesConfig {
  areaEnabled: boolean;
  games: Record<string, GameRec>;
  catalog: GameCatalogEntry[];
  savedBy: string | null;
  savedAt: number | null;
}

export interface GoogleAiConnectionConfig {
  provider: "gemini";
  label: string;
  enabled: boolean;
  source: "panel" | "environment" | "mixed" | "missing";
  clientId: string;
  clientSecretConfigured: boolean;
  redirectUri: string;
  scopes: string[];
  projectId: string;
  savedBy: string | null;
  savedAt: number | null;
  help: {
    studentFlow: string;
    redirectExamples: string[];
  };
}

// Tipos de Usuários (manager-users).
export interface UserCompanyMembership {
  companyId: string;
  companyName: string;
  role: string;
}
export interface UserRow {
  id: string;
  email: string;
  name: string;
  apelido: string | null;
  tags: string[];
  flags: Record<string, boolean>;
  mustChangePassword: boolean;
  suspendedAt: string | null;
  /** Cota de armazenamento em bytes (caderno docs). null = default global
   *  (100 MB). [[project-caderno-storage-quota]] */
  storageQuotaBytes: number | null;
  /** Bytes em uso (SUM de todos os docs do aluno). */
  storageUsedBytes: number;
  createdAt: string;
  platformRoles: string[];
  companies: UserCompanyMembership[];
}
export interface CompanyMin {
  id: string;
  name: string;
}
export interface CreateUserBody {
  email: string;
  name: string;
  password: string;
  apelido?: string | null;
  tags?: string[];
  flags?: Record<string, boolean>;
  mustChangePassword?: boolean;
  platformAdmin?: boolean;
  companyMembership?: { companyId: string; role: "admin_company" | "teacher" | "student" };
  sendWelcomeEmail?: boolean;
}
export interface PatchUserBody {
  name?: string;
  email?: string;
  apelido?: string | null;
  tags?: string[];
  flags?: Record<string, boolean>;
  mustChangePassword?: boolean;
  suspended?: boolean;
  password?: string;
  platformAdmin?: boolean;
  companyMembership?: { companyId: string; role: "admin_company" | "teacher" | "student" };
  removeCompanyMembership?: { companyId: string; role?: string };
}

// Tipos de Organizações (manager-companies).
export interface CompanyListRow {
  id: string;
  slug: string;
  name: string;
  isAutonomous: boolean;
  autonomousUserId: string | null;
  autonomousUserEmail: string | null;
  pixKey: string | null;
  pixKeyType: string | null;
  pixRecipientName: string | null;
  pixCity: string | null;
  settings: Record<string, unknown>;
  adminCount: number;
  teacherCount: number;
  studentCount: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface CompanyMember {
  userId: string;
  email: string;
  name: string;
  apelido: string | null;
  role: "admin_company" | "teacher" | "student";
  grantedAt: string;
}
export interface CompanyDetail extends CompanyListRow {
  members: CompanyMember[];
}
export interface CreateCompanyBody {
  name: string;
  slug?: string;
  isAutonomous?: boolean;
  settings?: Record<string, unknown>;
  adminUserId?: string;
  adminUser?: { email: string; name: string; password: string };
  pixKey?: string | null;
  pixKeyType?: "email" | "cpf" | "cnpj" | "phone" | "random" | null;
  pixRecipientName?: string | null;
  pixCity?: string | null;
}
export interface PatchCompanyBody {
  name?: string;
  slug?: string;
  settings?: Record<string, unknown>;
  archived?: boolean;
  pixKey?: string | null;
  pixKeyType?: "email" | "cpf" | "cnpj" | "phone" | "random" | null;
  pixRecipientName?: string | null;
  pixCity?: string | null;
}

// Tipos de Grupos (manager-groups).
export interface GroupListRow {
  id: string;
  name: string;
  description: string | null;
  companyId: string | null;
  companyName: string | null;
  ownerUserId: string | null;
  ownerEmail: string | null;
  ownerName: string | null;
  tags: string[];
  memberCount: number;
  bankCount: number;
  createdAt: string;
  updatedAt: string;
  createdByEmail: string | null;
}
/** Linha de Grupo de Estudo (study_groups, peer-led) — distinto de GroupListRow (turma). */
export interface StudyGroupListRow {
  id: string;
  name: string;
  description: string | null;
  coverColor: string | null;
  coverIcon: string | null;
  hideMemberInfo: boolean;
  memberCount: number;
  itemCount: number;
  folderCount: number;
  createdByName: string | null;
  createdAt: string;
  archivedAt: string | null;
}

export interface GroupMember {
  id: string;
  email: string;
  name: string;
  apelido: string | null;
}
export interface GroupBank {
  id: string;
  name: string;
}
export interface GroupDetail extends GroupListRow {
  bankIds: string[];
  members: GroupMember[];
  banks: GroupBank[];
}
export interface CreateGroupBody {
  name: string;
  description?: string | null;
  companyId?: string | null;
  ownerUserId?: string | null;
  tags?: string[];
  bankIds?: string[];
  members?: string[];
}
export interface PatchGroupBody {
  name?: string;
  description?: string | null;
  companyId?: string | null;
  ownerUserId?: string | null;
  tags?: string[];
  bankIds?: string[];
  members?: string[]; // substituição total
  addMembers?: string[];
  removeMembers?: string[];
}
export interface SyncGroupGrantsResult {
  ok: true;
  members: number;
  banks: number;
  granted: number;
  skipped: number;
  message?: string;
}

// Tipos de Bank Grants (manager-bank-grants).
export interface BankGrantUserEntry {
  userId: string;
  email: string;
  name: string;
  apelido: string | null;
  canTakeExam: boolean;
  grantedAt: string;
}
export interface BankGrantBankEntry {
  bankId: string;
  name: string;
  archived: boolean;
  canTakeExam: boolean;
  grantedAt: string;
}
export interface BankGrantsByBank {
  bank: { id: string; name: string };
  entries: BankGrantUserEntry[];
}
export interface BankGrantsByUser {
  user: { id: string; email: string; name: string };
  entries: BankGrantBankEntry[];
}

// Tipos de Professores (manager-professors).
export interface ProfessorRow {
  id: string;
  email: string;
  name: string;
  companies: Array<{ companyId: string; companyName: string }>;
  disciplinasCount: number;
  bancosCount: number;
  lastSessionAt: string | null;
}

// Tipos de Bancos (manager-banks).
export interface BankListItem {
  id: string;
  name: string;
  description: string | null;
  companyId: string | null;
  companyName: string | null;
  ownerUserId: string | null;
  ownerEmail: string | null;
  isPublicToCompany: boolean;
  /** "private" (default), "unlisted" (link direto) ou "public" (Biblioteca). */
  visibility: "private" | "unlisted" | "public";
  /** Pasta da Biblioteca de Provas (admin-curada). NULL = "Sem categoria". */
  libraryFolderId: string | null;
  tags: string[];
  version: number;
  archivedAt: string | null;
  questionCount: number;
  /** Quantas questões dissertativas paralelas (geradas por IA) o banco
   *  tem. 0 = não tem versão dissertativa ainda. > 0 = aluno pode alternar
   *  no toggle do BankDetailView. */
  dissertativeCount: number;
  createdAt: string;
  updatedAt: string;
}
export interface UploadBankBody {
  id?: string;
  name: string;
  description?: string | null;
  companyId?: string | null;
  ownerUserId?: string;
  data: unknown; // array de questoes ou { questoes: [...] }
  tags?: string[];
  isPublicToCompany?: boolean;
  /** Visibilidade da Biblioteca de Provas: private (só dono+grants),
   *  unlisted (link direto), public (na biblioteca global). Default
   *  backend: private. [[project-admin-bank-upload-visibility-grants]] */
  visibility?: "private" | "unlisted" | "public";
  /** Pasta da Biblioteca onde o banco deve aparecer (só relevante quando
   *  visibility === "public"). Backend aceita null pra "sem pasta".
   *  [[project-biblioteca-de-provas]] */
  libraryFolderId?: string | null;
  /** Emails pra criar grants individuais junto com o banco. Backend
   *  resolve email→userId e cria bank_user_grants pra cada um. */
  grantEmails?: string[];
  /** Quando true em uma edição (id fornecido), salva sem incrementar version
   *  e SEM notificar grantees. Útil pra ajustes cosméticos (typo, ortografia)
   *  que não justificam disparar email/notif pros alunos. [[project-question-wysiwyg-editor]] */
  silent?: boolean;
}
export interface PatchBankBody {
  name?: string;
  description?: string | null;
  isPublicToCompany?: boolean;
  visibility?: "private" | "unlisted" | "public";
  libraryFolderId?: string | null;
  tags?: string[];
  archived?: boolean;
  ownerUserId?: string;
  companyId?: string | null;
}

export interface LibraryFolderRow {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  position: number;
  defaultVisibility: "private" | "unlisted" | "public";
  bankCountTotal: number;
  bankCountPublic: number;
  /** Quantos essay_themes estão nesta pasta (total/qualquer status). */
  essayCountTotal: number;
  /** Quantos essay_themes publicados+públicos estão nesta pasta. */
  essayCountPublic: number;
  createdAt: string;
  updatedAt: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {})
    },
    ...init
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const err = body as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      res.status,
      err?.error?.code ?? "http_error",
      err?.error?.message ?? `HTTP ${res.status}`
    );
  }
  return body as T;
}

export interface ReferralSummary {
  totalUsers: number;
  totalReferred: number;
  totalIndicators: number;
  conversionPct: number;
}

export interface ReferralTopRow {
  userId: string;
  displayName: string;
  referralCode: string | null;
  suspended: boolean;
  countReferred: number;
  countPaying: number;
}

export interface ReferralIndicatedRow {
  id: string;
  displayName: string;
  suspended: boolean;
  createdAt: string;
  referredAt: string | null;
  paidCentavos: number;
  hasActiveSub: boolean;
}

export const api = {
  me: () => call<MeResponse>("/api/auth/me"),
  // Referrals — relatórios admin
  referralsOverview: () =>
    call<{ summary: ReferralSummary; topIndicators: ReferralTopRow[] }>(
      "/api/manager/referrals"
    ),
  referralsForUser: (userId: string) =>
    call<{ indicated: ReferralIndicatedRow[]; total: number }>(
      `/api/manager/referrals?userId=${encodeURIComponent(userId)}`
    ),
  logout: () => call<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  switchContext: (ctx: { kind: "platform" | "company"; role: string; companyId?: string }) =>
    call<{ ok: true }>("/api/auth/switch-context", {
      method: "POST",
      body: JSON.stringify(ctx)
    }),
  // Recado / anúncio
  getAnnouncement: () => call<AnnouncementRecord>("/api/manager/announcement"),
  getAnnouncementHistory: () =>
    call<{ history: AnnouncementRecord[] }>("/api/manager/announcement?history=1"),
  saveAnnouncement: (rec: {
    enabled: boolean;
    title: string;
    text: string;
    tone: string;
    audience: AnnouncementAudience;
  }) =>
    call<AnnouncementRecord>("/api/manager/announcement", {
      method: "PUT",
      body: JSON.stringify(rec)
    }),
  // Jogos config
  getGamesConfig: () => call<GamesConfig>("/api/manager/games-config"),
  saveGamesConfig: (cfg: {
    areaEnabled: boolean;
    games: Record<string, GameRec>;
  }) =>
    call<GamesConfig>("/api/manager/games-config", {
      method: "PUT",
      body: JSON.stringify(cfg)
    }),
  getGoogleAiConnectionConfig: () =>
    call<GoogleAiConnectionConfig>("/api/manager/ai-connections-config"),
  saveGoogleAiConnectionConfig: (cfg: {
    clientId: string;
    clientSecret?: string;
    redirectUri: string;
    projectId?: string;
    scopes: string[] | string;
  }) =>
    call<GoogleAiConnectionConfig>("/api/manager/ai-connections-config", {
      method: "PUT",
      body: JSON.stringify(cfg)
    }),
  // Banks
  listBanks: () => call<{ banks: BankListItem[] }>("/api/manager/banks"),
  getBank: (id: string) =>
    call<{
      bank: { id: string; name: string; version: number };
      questoes: unknown[];
      questoesDissertativas: unknown[];
    }>(`/api/manager/banks?id=${encodeURIComponent(id)}`),
  uploadBank: (data: UploadBankBody) =>
    call<{ bank: { id: string; name: string; version: number }; count: number }>(
      "/api/manager/banks",
      {
        method: "POST",
        body: JSON.stringify(data)
      }
    ),
  patchBank: (id: string, updates: PatchBankBody) =>
    call<{
      bank: {
        id: string;
        name: string;
        archivedAt: string | null;
        tags: string[];
        isPublicToCompany: boolean;
        updatedAt: string;
      };
    }>("/api/manager/banks", {
      method: "PATCH",
      body: JSON.stringify({ id, ...updates })
    }),
  deleteBank: (id: string) =>
    call<{ deleted: string; name: string }>(
      `/api/manager/banks?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // Library folders (Biblioteca de Provas)
  listLibraryFolders: () =>
    call<{ folders: LibraryFolderRow[] }>("/api/manager/library-folders"),
  createLibraryFolder: (data: { name: string; parentId?: string | null; defaultVisibility?: string; position?: number }) =>
    call<{ folder: LibraryFolderRow }>("/api/manager/library-folders", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchLibraryFolder: (
    id: string,
    updates: {
      name?: string;
      parentId?: string | null;
      defaultVisibility?: "private" | "unlisted" | "public";
      position?: number;
      applyCascade?: boolean;
    }
  ) =>
    call<{ folder: LibraryFolderRow; cascadedBanks: number }>(
      `/api/manager/library-folders?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(updates) }
    ),
  deleteLibraryFolder: (id: string) =>
    call<{ deleted: { id: string; name: string } }>(
      `/api/manager/library-folders?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  /** Lista bancos dentro de uma pasta. Use `folderId="none"` pra ver órfãos
   *  (bancos sem pasta = libraryFolderId IS NULL). */
  listLibraryBanksInFolder: (folderId: string) =>
    call<{
      banks: Array<{
        id: string;
        name: string;
        description: string | null;
        companyId: string | null;
        companyName: string | null;
        visibility: "private" | "unlisted" | "public";
        libraryFolderId: string | null;
        archivedAt: string | null;
        updatedAt: string;
      }>;
    }>(
      `/api/manager/library-folders?action=banks&folderId=${encodeURIComponent(folderId)}`
    ),
  /** Lista essay_themes (redações) dentro de uma pasta. `folderId="none"`
   *  pra ver órfãos. Mesma semântica de listLibraryBanksInFolder. */
  listLibraryEssaysInFolder: (folderId: string) =>
    call<{
      essays: Array<{
        id: string;
        slug: string;
        title: string;
        displayName: string;
        banca: string;
        year: number | null;
        phase: string | null;
        edition: string | null;
        visibility: "private" | "unlisted" | "public";
        status: "draft" | "published" | "archived";
        libraryFolderId: string | null;
        archivedAt: string | null;
        updatedAt: string;
      }>;
    }>(
      `/api/manager/library-folders?action=essays&folderId=${encodeURIComponent(folderId)}`
    ),
  /** Lista TODOS os bancos pra picker de "adicionar bancos a esta pasta". */
  listAllBanksForLibrary: () =>
    call<{
      banks: Array<{
        id: string;
        name: string;
        companyName: string | null;
        visibility: "private" | "unlisted" | "public";
        libraryFolderId: string | null;
        archivedAt: string | null;
      }>;
    }>("/api/manager/library-folders?action=all-banks"),
  /** Move/Atribui N bancos pra uma pasta (ou pra "sem pasta" se folderId=null). */
  bulkAssignLibraryBanks: (folderId: string | null, bankIds: string[]) =>
    call<{ moved: number; folderId: string | null }>(
      "/api/manager/library-folders?action=bulk-assign",
      { method: "POST", body: JSON.stringify({ folderId, bankIds }) }
    ),
  // Usuários
  listUsers: () =>
    call<{ users: UserRow[]; companies: CompanyMin[] }>("/api/manager/users"),
  createUser: (data: CreateUserBody) =>
    call<{
      user: { id: string; email: string; name: string };
      emailSent?: boolean;
      emailReason?: string | null;
    }>("/api/manager/users", { method: "POST", body: JSON.stringify(data) }),
  patchUser: (id: string, updates: PatchUserBody) =>
    call<{ ok: true }>("/api/manager/users", {
      method: "PATCH",
      body: JSON.stringify({ id, ...updates })
    }),
  resetUserPassword: (id: string) =>
    call<{
      ok: true;
      emailSent: boolean;
      emailReason: string | null;
      inviterName: string | null;
    }>(
      `/api/manager/users?action=reset-password&id=${encodeURIComponent(id)}`,
      { method: "POST" }
    ),
  /** Desbloqueia conta travada por excesso de tentativas, sem trocar a senha
   *  do usuário. [[project-account-lockout]] */
  unlockUserPassword: (id: string) =>
    call<{ ok: true; wasLocked: boolean }>(
      `/api/manager/users?action=unlock-password&id=${encodeURIComponent(id)}`,
      { method: "POST" }
    ),
  listProfessors: () =>
    call<{ professors: ProfessorRow[] }>("/api/manager/professors"),
  // Grupos
  listGroups: () =>
    call<{ groups: GroupListRow[] }>("/api/manager/groups"),
  /** Grupos de Estudo peer-led (study_groups) — diferente de `groups` (turma).
   *  Usado pra anexar a signup_allowlist e pra gestão admin. */
  listStudyGroups: () =>
    call<{ groups: StudyGroupListRow[] }>("/api/manager/study-groups"),
  getStudyGroup: (id: string) =>
    call<{
      group: {
        id: string;
        name: string;
        description: string | null;
        coverColor: string | null;
        coverIcon: string | null;
        hideMemberInfo: boolean;
        createdBy: string | null;
        createdAt: string;
        updatedAt: string;
        archivedAt: string | null;
      };
      members: Array<{
        userId: string;
        role: "owner" | "member";
        joinedAt: string;
        name: string;
        email: string;
      }>;
      items: Array<{
        id: string;
        kind: "folder" | "bank_ref" | "file" | "note_ref" | "exam_ref";
        parentId: string | null;
        name: string;
        bankId: string | null;
        position: number;
        addedAt: string;
      }>;
    }>(`/api/manager/study-groups?id=${encodeURIComponent(id)}`),
  createStudyGroup: (data: {
    name: string;
    description?: string | null;
    hideMemberInfo?: boolean;
    ownerUserIds?: string[];
  }) =>
    call<{ group: { id: string; name: string } }>("/api/manager/study-groups", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchStudyGroup: (
    id: string,
    updates: {
      name?: string;
      description?: string | null;
      hideMemberInfo?: boolean;
    }
  ) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(updates) }
    ),
  deleteStudyGroup: (id: string) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  studyGroupAddBank: (id: string, bankId: string) =>
    call<{ item: { id: string } }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=add-bank`,
      { method: "POST", body: JSON.stringify({ bankId }) }
    ),
  studyGroupRemoveBank: (id: string, bankId: string) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=remove-bank`,
      { method: "POST", body: JSON.stringify({ bankId }) }
    ),
  studyGroupAddMember: (
    id: string,
    payload: { userId?: string; email?: string; role?: "owner" | "member" }
  ) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=add-member`,
      { method: "POST", body: JSON.stringify(payload) }
    ),
  studyGroupRemoveMember: (id: string, userId: string) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=remove-member`,
      { method: "POST", body: JSON.stringify({ userId }) }
    ),
  studyGroupSetRole: (
    id: string,
    userId: string,
    role: "owner" | "member"
  ) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=${role === "owner" ? "promote" : "demote"}`,
      { method: "POST", body: JSON.stringify({ userId }) }
    ),
  studyGroupCreateFolder: (
    id: string,
    payload: { name: string; parentId?: string | null }
  ) =>
    call<{ item: { id: string } }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=create-folder`,
      { method: "POST", body: JSON.stringify(payload) }
    ),
  studyGroupRenameItem: (id: string, itemId: string, name: string) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=rename-item`,
      { method: "POST", body: JSON.stringify({ itemId, name }) }
    ),
  studyGroupDeleteItem: (id: string, itemId: string) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=delete-item`,
      { method: "POST", body: JSON.stringify({ itemId }) }
    ),
  studyGroupMoveItem: (
    id: string,
    itemId: string,
    parentId: string | null
  ) =>
    call<{ ok: boolean }>(
      `/api/manager/study-groups?id=${encodeURIComponent(id)}&action=move-item`,
      { method: "POST", body: JSON.stringify({ itemId, parentId }) }
    ),
  getGroup: (id: string) =>
    call<{ group: GroupDetail }>(`/api/manager/groups?id=${encodeURIComponent(id)}`),
  createGroup: (data: CreateGroupBody) =>
    call<{ group: GroupDetail }>("/api/manager/groups", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchGroup: (id: string, updates: PatchGroupBody) =>
    call<{ group: GroupDetail }>(
      `/api/manager/groups?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(updates) }
    ),
  deleteGroup: (id: string) =>
    call<{ deleted: string; name: string }>(
      `/api/manager/groups?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  applyGroupTags: (id: string) =>
    call<{ ok: true; tags: number; touched: number }>(
      `/api/manager/groups?id=${encodeURIComponent(id)}&action=apply-tags`,
      { method: "POST" }
    ),
  syncGroupGrants: (id: string) =>
    call<SyncGroupGrantsResult>(
      `/api/manager/groups?id=${encodeURIComponent(id)}&action=sync-grants`,
      { method: "POST" }
    ),
  // Organizações
  listCompanies: () =>
    call<{ companies: CompanyListRow[] }>("/api/manager/companies"),
  getCompany: (id: string) =>
    call<{ company: CompanyDetail }>(
      `/api/manager/companies?id=${encodeURIComponent(id)}`
    ),
  createCompany: (data: CreateCompanyBody) =>
    call<{ company: CompanyDetail }>("/api/manager/companies", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchCompany: (id: string, updates: PatchCompanyBody) =>
    call<{ company: CompanyDetail }>(
      `/api/manager/companies?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(updates) }
    ),
  archiveCompany: (id: string) =>
    call<{ archived: string; name: string }>(
      `/api/manager/companies?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  addCompanyMember: (
    companyId: string,
    userId: string,
    role: "admin_company" | "teacher" | "student"
  ) =>
    call<{ company: CompanyDetail }>(
      `/api/manager/companies?id=${encodeURIComponent(companyId)}&action=add-member`,
      {
        method: "POST",
        body: JSON.stringify({ userId, role })
      }
    ),
  removeCompanyMember: (
    companyId: string,
    userId: string,
    role: "admin_company" | "teacher" | "student"
  ) =>
    call<{ company: CompanyDetail }>(
      `/api/manager/companies?id=${encodeURIComponent(companyId)}&memberUserId=${encodeURIComponent(userId)}&memberRole=${encodeURIComponent(role)}`,
      { method: "DELETE" }
    ),
  // Bank Grants
  grantsByBank: (bankId: string) =>
    call<BankGrantsByBank>(
      `/api/manager/bank-grants?bankId=${encodeURIComponent(bankId)}`
    ),
  grantsByUser: (userId: string) =>
    call<BankGrantsByUser>(
      `/api/manager/bank-grants?userId=${encodeURIComponent(userId)}`
    ),
  putGrant: (data: { bankId: string; userId: string; canTakeExam?: boolean }) =>
    call<{
      bankId: string;
      userId: string;
      canTakeExam: boolean;
      grantedAt: string;
    }>("/api/manager/bank-grants", {
      method: "PUT",
      body: JSON.stringify(data)
    }),
  deleteGrant: (bankId: string, userId: string) =>
    call<{ ok: true }>(
      `/api/manager/bank-grants?bankId=${encodeURIComponent(bankId)}&userId=${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    ),
  // ===== Estudar com IA (admin de plataforma) =====
  listAiStudyAccess: () =>
    call<{ accesses: AiStudyAccessRow[] }>("/api/manager/ai-study-access"),
  grantAiStudyAccess: (email: string, notes?: string) =>
    call<{ ok: true; userId: string; email: string }>(
      "/api/manager/ai-study-access",
      { method: "POST", body: JSON.stringify({ email, notes: notes ?? "" }) }
    ),
  revokeAiStudyAccess: (userId: string) =>
    call<{ revoked: string }>(
      `/api/manager/ai-study-access?userId=${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    ),
  // ===== Signup allowlist (admin) =====
  listSignupAllowlist: () =>
    call<{ items: SignupAllowlistRow[] }>("/api/manager/signup-allowlist"),
  addSignupAllowlist: (data: {
    emails: string[];
    targetCompanyId?: string | null;
    targetRole?: "admin_company" | "teacher" | "student" | null;
    targetGroupIds?: string[];
    targetStudyGroupIds?: string[];
    note?: string;
    sendInvite?: boolean;
  }) =>
    call<{
      added: { email: string }[];
      skipped: { email: string; reason: string }[];
      emailResults?: { email: string; ok: boolean; reason?: string }[];
    }>("/api/manager/signup-allowlist", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  revokeSignupAllowlist: (id: string) =>
    call<{ ok: true }>(
      `/api/manager/signup-allowlist?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  resendSignupAllowlist: (id: string) =>
    call<{ ok: boolean; reason: string | null }>(
      `/api/manager/signup-allowlist?action=resend-invite&id=${encodeURIComponent(id)}`,
      { method: "POST" }
    ),
  getSidebarDefaults: () =>
    call<{
      defaults: Partial<Record<"student" | "teacher" | "company" | "manager", string[]>>;
    }>("/api/manager/sidebar-defaults"),
  updateSidebarDefaults: (
    defaults: Partial<Record<"student" | "teacher" | "company" | "manager", string[]>>
  ) =>
    call<{
      defaults: Partial<Record<"student" | "teacher" | "company" | "manager", string[]>>;
    }>("/api/manager/sidebar-defaults", {
      method: "PUT",
      body: JSON.stringify(defaults)
    }),
  listAiUsersStatus: () =>
    call<{
      users: Array<{
        id: string;
        email: string;
        name: string;
        googleConnected: boolean;
        googleEmail: string | null;
        lastAiUsage: {
          source: "platform" | "byok" | "connected";
          provider: string;
          model: string;
          modelKind?: string;
          atIso: string;
          feature?: string;
        } | null;
        isSuspended: boolean;
      }>;
      featureConfigs: Array<{
        featureKey: string;
        provider: string;
        model: string;
        apiKeySet: boolean;
        updatedAt: string | null;
      }>;
    }>("/api/manager/ai-users-status"),
  // ===== Payments config (admin global) =====
  getPaymentsConfig: () =>
    call<{ config: PaymentsConfig }>("/api/manager/payments-config"),
  updatePaymentsConfig: (data: Partial<PaymentsConfig>) =>
    call<{ config: PaymentsConfig }>("/api/manager/payments-config", {
      method: "PUT",
      body: JSON.stringify(data)
    }),
  listPlatformPlans: () =>
    call<{ plans: PlatformPlanRow[] }>("/api/manager/platform-plans"),
  createPlatformPlan: (data: {
    slug?: string;
    name: string;
    description?: string;
    monthlyCents?: number;
    features?: Record<string, unknown>;
    displayOrder?: number;
  }) =>
    call<{ plan: PlatformPlanRow }>("/api/manager/platform-plans", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  updatePlatformPlan: (data: {
    id: string;
    name?: string;
    description?: string;
    monthlyCents?: number;
    features?: Record<string, unknown>;
    isActive?: boolean;
    isPublic?: boolean;
    isDefault?: boolean;
    displayOrder?: number;
  }) =>
    call<{ plan: PlatformPlanRow }>("/api/manager/platform-plans", {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  deletePlatformPlan: (id: string) =>
    call<{ ok: true }>(
      `/api/manager/platform-plans?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  /** Arquiva (soft) ou exclui (hard) um plano, migrando assinaturas ativas
   *  pra `targetSlug` quando informado. Server bloqueia hard-delete se ainda
   *  houver subs históricas referenciando o slug. */
  archiveOrDeletePlan: (data: {
    id: string;
    targetSlug: string | null;
    hard: boolean;
  }) =>
    call<{ ok: true; mode: "archive" | "hard"; migrated: number }>(
      `/api/manager/platform-plans?id=${encodeURIComponent(data.id)}`,
      {
        method: "DELETE",
        body: JSON.stringify({
          targetSlug: data.targetSlug,
          hard: data.hard
        })
      }
    ),
  assignPlan: (data: {
    userId: string;
    slug: string;
    billingType?: "pix_monthly" | "credit_card";
  }) =>
    call<{ ok: true }>("/api/manager/assign-plan", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  listOpenCharges: (status?: "pending" | "failed") => {
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    const qs = q.toString();
    return call<{ charges: OpenChargeRow[] }>(
      `/api/manager/subscription-charges${qs ? "?" + qs : ""}`
    );
  },
  markChargeAsPaid: (id: string, note?: string) =>
    call<{ ok: true; alreadyPaid?: boolean }>(
      `/api/manager/subscription-charges?id=${encodeURIComponent(id)}&action=mark-paid`,
      { method: "PATCH", body: JSON.stringify({ note: note ?? "" }) }
    ),
  listPlanMembers: (slug: string) =>
    call<{
      plan: PlatformPlanRow;
      members: PlanMemberRow[];
      summary: { total: number; active: number; pastDue: number; paused: number };
      note?: string;
    }>(`/api/manager/plan-members?slug=${encodeURIComponent(slug)}`),
  listEmailTemplates: () =>
    call<{ templates: EmailTemplateRow[] }>("/api/manager/email-templates"),
  updateEmailTemplate: (data: {
    id: string;
    subject?: string;
    html?: string;
    textBody?: string;
    isActive?: boolean;
  }) =>
    call<{ template: EmailTemplateRow }>("/api/manager/email-templates", {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  resetEmailTemplate: (id: string) =>
    call<{ template: EmailTemplateRow }>(
      `/api/manager/email-templates?id=${encodeURIComponent(id)}&action=reset`,
      { method: "POST" }
    ),
  sendEmailTest: (slug: string, to: string) =>
    call<{ ok: true; providerMessageId?: string; to: string }>(
      `/api/manager/email-test-send?slug=${encodeURIComponent(slug)}`,
      { method: "POST", body: JSON.stringify({ to }) }
    ),
  getEmailConfig: () =>
    call<{
      config: EmailConfigRow;
      runtime: EmailRuntimeStatus;
    }>("/api/manager/email-config"),
  updateEmailConfig: (data: {
    resendApiKey?: string;
    resendFrom?: string;
    resendDefaultDomain?: string;
  }) =>
    call<{ config: EmailConfigRow }>("/api/manager/email-config", {
      method: "PUT",
      body: JSON.stringify(data)
    }),
  listEmailDomains: () =>
    call<{ status: number; result: unknown }>("/api/manager/email-domains"),
  addEmailDomain: (name: string, region?: string) =>
    call<{ status: number; result: unknown }>("/api/manager/email-domains", {
      method: "POST",
      body: JSON.stringify({ name, region })
    }),
  verifyEmailDomain: (id: string) =>
    call<{ status: number; result: unknown }>(
      `/api/manager/email-domains?action=verify&id=${encodeURIComponent(id)}`,
      { method: "POST" }
    ),
  deleteEmailDomain: (id: string) =>
    call<{ status: number; result: unknown }>(
      `/api/manager/email-domains?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  getEmailDomain: (id: string) =>
    call<{ status: number; result: unknown }>(
      `/api/manager/email-domains?id=${encodeURIComponent(id)}`
    ),
  listFeatureFlags: () =>
    call<{ flags: FeatureFlagRow[] }>("/api/manager/feature-flags"),
  getFeatureFlag: (id: string) =>
    call<{
      flag: FeatureFlagRow;
      users: Array<{
        userId: string;
        userName: string;
        userEmail: string;
        addedAt: string;
      }>;
      groups: Array<{ groupId: string; groupName: string; addedAt: string }>;
      usersDenied: Array<{
        userId: string;
        userName: string;
        userEmail: string;
        addedAt: string;
      }>;
    }>(`/api/manager/feature-flags?id=${encodeURIComponent(id)}`),
  createFeatureFlag: (data: {
    key: string;
    label: string;
    description?: string;
    platform?: FeatureFlagPlatform;
    audience?: FeatureFlagAudience;
    status?: FeatureFlagStatus;
  }) =>
    call<{ flag: FeatureFlagRow }>("/api/manager/feature-flags", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  updateFeatureFlag: (
    id: string,
    data: {
      label?: string;
      description?: string;
      platform?: FeatureFlagPlatform;
      audience?: FeatureFlagAudience;
      status?: FeatureFlagStatus;
    }
  ) =>
    call<{ flag: FeatureFlagRow }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  /** Adiciona múltiplos usuários à allowlist via lista de emails. Resolve por
   *  email exato OU email_normalized (dedup tipo Gmail). Retorna contadores
   *  de adicionados/já-eram-membros + lista de emails não encontrados. */
  bulkAddFlagUsers: (flagId: string, emails: string[]) =>
    call<{
      ok: true;
      added: number;
      alreadyIn: number;
      notFound: string[];
    }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(flagId)}&action=add-users-bulk`,
      { method: "POST", body: JSON.stringify({ emails }) }
    ),
  deleteFeatureFlag: (id: string) =>
    call<{ ok: true }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  addFlagUser: (flagId: string, userId: string) =>
    call<{ ok: true }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(
        flagId
      )}&action=add-user&userId=${encodeURIComponent(userId)}`,
      { method: "POST" }
    ),
  removeFlagUser: (flagId: string, userId: string) =>
    call<{ ok: true }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(
        flagId
      )}&action=remove-user&userId=${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    ),
  addFlagUserDenied: (flagId: string, userId: string) =>
    call<{ ok: true }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(
        flagId
      )}&action=add-user-denied&userId=${encodeURIComponent(userId)}`,
      { method: "POST" }
    ),
  removeFlagUserDenied: (flagId: string, userId: string) =>
    call<{ ok: true }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(
        flagId
      )}&action=remove-user-denied&userId=${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    ),
  addFlagGroup: (flagId: string, groupId: string) =>
    call<{ ok: true }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(
        flagId
      )}&action=add-group&groupId=${encodeURIComponent(groupId)}`,
      { method: "POST" }
    ),
  removeFlagGroup: (flagId: string, groupId: string) =>
    call<{ ok: true }>(
      `/api/manager/feature-flags?id=${encodeURIComponent(
        flagId
      )}&action=remove-group&groupId=${encodeURIComponent(groupId)}`,
      { method: "DELETE" }
    ),
  listSubscriptions: (params?: {
    status?: "active" | "past_due" | "paused" | "canceled";
    billingType?: "pix_monthly" | "credit_card";
    scope?: "platform" | "company";
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.billingType) q.set("billingType", params.billingType);
    if (params?.scope) q.set("scope", params.scope);
    const qs = q.toString();
    return call<{
      items: PlatformSubscriptionRow[];
      summary: PlatformSubscriptionSummary;
    }>(`/api/manager/subscriptions${qs ? "?" + qs : ""}`);
  },

  // ============================================
  // IA por feature — admin escolhe modelo+provider+chave por feature key
  // ============================================
  listAiFeatureConfigs: () =>
    call<{ configs: AiFeatureConfigRow[] }>(
      "/api/manager/ai-feature-config"
    ),
  upsertAiFeatureConfig: (data: {
    featureKey: string;
    provider: "gemini" | "openai" | "anthropic" | "xai";
    model: string;
    apiKey?: string; // vazio = preserva a anterior
    notes?: string;
  }) =>
    call<{ ok: true }>("/api/manager/ai-feature-config", {
      method: "PUT",
      body: JSON.stringify(data)
    }),
  deleteAiFeatureConfig: (featureKey: string) =>
    call<{ ok: true }>(
      `/api/manager/ai-feature-config?featureKey=${encodeURIComponent(featureKey)}`,
      { method: "DELETE" }
    ),

  // ============================================
  // Catálogo de tipos de notificação (toggle + history)
  // ============================================
  listNotificationTypes: () =>
    call<{ types: NotificationTypeRow[] }>(
      "/api/manager/notification-types"
    ),
  notificationTypesSummary: () =>
    call<{ summary: { kind: string; count: number; lastAt: string | null }[] }>(
      "/api/manager/notification-types?action=summary"
    ),
  notificationTypeHistory: (key: string) =>
    call<{ kind: string; history: NotificationHistoryRow[] }>(
      `/api/manager/notification-types?key=${encodeURIComponent(key)}&action=history`
    ),
  updateNotificationType: (
    key: string,
    patch: { isEnabled?: boolean; label?: string; description?: string; triggerDescription?: string }
  ) =>
    call<{ ok: true }>(
      `/api/manager/notification-types?key=${encodeURIComponent(key)}`,
      { method: "PATCH", body: JSON.stringify(patch) }
    ),

  // ===== Payment sandbox (MP) =====
  getPaymentSandboxConfig: () =>
    call<{
      configured: boolean;
      message?: string;
      publicKey?: string | null;
      payerEmail?: string;
    }>("/api/manager/payment-sandbox?action=config"),
  listPaymentSandbox: () =>
    call<{
      total: number;
      results: Array<{
        id: string;
        status: string;
        statusDetail: string;
        amountBrl: number;
        description: string;
        paymentMethod: string;
        externalReference: string;
        createdAt: string;
        approvedAt: string | null;
      }>;
    }>("/api/manager/payment-sandbox?action=list"),
  listPaymentSandboxWebhookEvents: () =>
    call<{
      events: Array<{
        id: string;
        externalEventId: string;
        eventType: string;
        status: string;
        matchedChargeId: string | null;
        receivedAt: string;
        processedAt: string | null;
        processError: string | null;
        rawPayload: unknown;
      }>;
    }>("/api/manager/payment-sandbox?action=webhook-events"),
  createPaymentSandboxPix: (data: { amountCents: number; description: string }) =>
    call<{
      pix: {
        paymentId: string;
        brcode: string;
        qrCodeBase64?: string;
        ticketUrl?: string;
        expiresAt: string;
        externalReference: string;
      };
    }>("/api/manager/payment-sandbox", {
      method: "POST",
      body: JSON.stringify({ action: "pix", ...data })
    }),
  createPaymentSandboxCard: (data: {
    amountCents: number;
    description: string;
    mpCardToken: string;
  }) =>
    call<
      | {
          ok: true;
          paymentId: string;
          status: string;
          externalReference: string;
        }
      | { ok: false; status: "failed"; reason: string }
    >("/api/manager/payment-sandbox", {
      method: "POST",
      body: JSON.stringify({ action: "card", ...data })
    }),

  // [[project-quickstudy]] — limites do Tira-dúvida rápido (qtd/tamanho imagens, master switch)
  getQuickStudyConfig: () =>
    call<{
      settings: {
        enabled: boolean;
        maxImagesPerMessage: number;
        maxImageSizeMb: number;
        maxTextChars: number;
      };
    }>("/api/manager/quick-study-config"),
  setQuickStudyConfig: (patch: {
    enabled?: boolean;
    maxImagesPerMessage?: number;
    maxImageSizeMb?: number;
    maxTextChars?: number;
  }) =>
    call<{
      settings: {
        enabled: boolean;
        maxImagesPerMessage: number;
        maxImageSizeMb: number;
        maxTextChars: number;
      };
    }>("/api/manager/quick-study-config", {
      method: "PUT",
      body: JSON.stringify(patch)
    }),
  getQuickStudyIncidents: (opts: { days?: number; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.days) params.set("days", String(opts.days));
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return call<{
      incidents: Array<{
        messageId: string;
        conversationId: string;
        conversationTitle: string;
        personaLabel: string | null;
        assistantContent: string;
        model: string | null;
        createdAt: string;
        user: { id: string; name: string | null; email: string | null };
      }>;
      windowDays: number;
      total: number;
    }>(`/api/manager/quick-study-incidents${qs ? `?${qs}` : ""}`);
  },

  // [[lib/credits-guard.ts]] — admin liga/desliga verificação de créditos
  // por feature de IA. Default OFF (libera tudo).
  getAiCreditsGate: () =>
    call<{
      settings: { enabledGlobal: boolean; features: Record<string, boolean> };
      catalog: Array<{
        slug: string;
        label: string;
        group: string;
        description: string;
      }>;
    }>("/api/manager/ai-credits-gate"),
  setAiCreditsGate: (patch: {
    enabledGlobal?: boolean;
    features?: Record<string, boolean>;
  }) =>
    call<{
      settings: { enabledGlobal: boolean; features: Record<string, boolean> };
      catalog: Array<{
        slug: string;
        label: string;
        group: string;
        description: string;
      }>;
    }>("/api/manager/ai-credits-gate", {
      method: "PUT",
      body: JSON.stringify(patch)
    }),

  // [[project-admin-default-orphan-org]] — admin escolhe pra onde caem
  // alunos sem vínculo a outra organização.
  getOrphanDefault: () =>
    call<{
      current: { id: string; slug: string; name: string } | null;
      options: Array<{ id: string; slug: string; name: string }>;
    }>("/api/manager/orphan-default"),
  setOrphanDefault: (slug: string) =>
    call<{ current: { id: string; slug: string; name: string } }>(
      "/api/manager/orphan-default",
      { method: "PUT", body: JSON.stringify({ slug }) }
    ),

  // [[project-student-ui-visibility]] — admin controla visibilidade de
  // partes do painel do aluno: rótulo de provider/modelo (platform/BYOK
  // separados) + seção Webhooks em Minha Conta.
  getStudentUiVisibility: () =>
    call<{
      settings: {
        aiMetaPlatform: boolean;
        aiMetaByok: boolean;
        webhooks: boolean;
      };
    }>("/api/manager/student-ui-visibility"),
  setStudentUiVisibility: (settings: {
    aiMetaPlatform: boolean;
    aiMetaByok: boolean;
    webhooks: boolean;
  }) =>
    call<{
      settings: {
        aiMetaPlatform: boolean;
        aiMetaByok: boolean;
        webhooks: boolean;
      };
    }>("/api/manager/student-ui-visibility", {
      method: "PUT",
      body: JSON.stringify(settings)
    }),

  // ============================================
  // User reports (bug + feature request)
  // ============================================
  listUserReports: (filters: {
    kind?: "bug" | "feature";
    status?: "open" | "in_progress" | "resolved" | "wontfix";
    limit?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (filters.kind) q.set("kind", filters.kind);
    if (filters.status) q.set("status", filters.status);
    if (filters.limit) q.set("limit", String(filters.limit));
    const qs = q.toString();
    return call<{ reports: AdminUserReport[] }>(
      `/api/manager/user-reports${qs ? `?${qs}` : ""}`
    );
  },
  getUserReport: (id: string) =>
    call<{ report: AdminUserReport }>(
      `/api/manager/user-reports?id=${encodeURIComponent(id)}`
    ),
  updateUserReport: (
    id: string,
    patch: {
      status?: "open" | "in_progress" | "resolved" | "wontfix";
      severity?: "low" | "medium" | "high" | "critical" | null;
    }
  ) =>
    call<{ report: AdminUserReport }>(
      `/api/manager/user-reports?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(patch) }
    ),
  respondUserReport: (
    id: string,
    body: {
      text: string;
      status?: "open" | "in_progress" | "resolved" | "wontfix";
    }
  ) =>
    call<{ report: AdminUserReport }>(
      `/api/manager/user-reports?id=${encodeURIComponent(id)}&action=respond`,
      { method: "POST", body: JSON.stringify(body) }
    ),

  // ============================================
  // FAQ (admin)
  // ============================================
  listFaq: () =>
    call<{ entries: AdminFaqEntry[] }>("/api/manager/faq"),
  createFaq: (input: {
    question: string;
    answer: string;
    category?: string | null;
    isPublished?: boolean;
  }) =>
    call<{ entry: AdminFaqEntry }>("/api/manager/faq", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  updateFaq: (
    id: string,
    patch: {
      question?: string;
      answer?: string;
      category?: string | null;
      isPublished?: boolean;
    }
  ) =>
    call<{ entry: AdminFaqEntry }>(
      `/api/manager/faq?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(patch) }
    ),
  deleteFaq: (id: string) =>
    call<{ ok: true }>(`/api/manager/faq?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  reorderFaq: (order: string[]) =>
    call<{ ok: true; count: number }>(
      "/api/manager/faq?action=reorder",
      { method: "POST", body: JSON.stringify({ order }) }
    ),
  getFaqConfig: () =>
    call<{ config: FaqConfig }>("/api/manager/faq-config"),
  saveFaqConfig: (isEnabled: boolean) =>
    call<{ config: FaqConfig }>("/api/manager/faq-config", {
      method: "PUT",
      body: JSON.stringify({ isEnabled })
    }),
  getBranding: () => call<{ config: BrandingConfig }>("/api/manager/branding"),
  saveBranding: (patch: Partial<BrandingConfig>) =>
    call<{ config: BrandingConfig }>("/api/manager/branding", {
      method: "PUT",
      body: JSON.stringify(patch)
    }),
  // ===== Legal / Consentimento (LGPD) — [[project-legal-consent-system]] =====
  legalList: () =>
    call<{ documents: Record<string, LegalDocument[]>; rollout: LegalRollout }>(
      "/api/manager/legal"
    ),
  legalSetRollout: (mode: LegalRollout["mode"], allowlist: string[]) =>
    call<{ rollout: LegalRollout }>("/api/manager/legal", {
      method: "PATCH",
      body: JSON.stringify({ mode, allowlist })
    }),
  legalSaveDraft: (body: {
    id?: string;
    kind: string;
    title: string;
    content: string;
    summary?: string | null;
  }) =>
    call<{ document: LegalDocument }>("/api/manager/legal", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  legalPublish: (id: string) =>
    call<{ document: LegalDocument }>("/api/manager/legal", {
      method: "PUT",
      body: JSON.stringify({ id })
    }),
  legalDeleteDraft: (id: string) =>
    call<{ ok: true }>(`/api/manager/legal?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  // ===== Monitoramento de erros — [[project-error-monitoring]] =====
  errorLogList: (opts?: { source?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.source) qs.set("source", opts.source);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    if (opts?.offset) qs.set("offset", String(opts.offset));
    const q = qs.toString();
    return call<{ total: number; rows: ErrorLogRow[] }>(
      `/api/manager/error-log${q ? "?" + q : ""}`
    );
  },
  errorLogClear: (olderThanDays: number) =>
    call<{ ok: true }>(
      `/api/manager/error-log?olderThanDays=${olderThanDays}`,
      { method: "DELETE" }
    ),
  // ===== Encerramentos de conta — [[project-account-closure]] =====
  accountClosuresList: (status?: string) =>
    call<{ rows: AccountClosureRow[] }>(
      `/api/manager/account-closures${status ? "?status=" + status : ""}`
    ),
  accountClosureAction: (id: string, action: "process" | "reactivate") =>
    call<{ ok: true; suspended?: boolean }>("/api/manager/account-closures", {
      method: "PATCH",
      body: JSON.stringify({ id, action })
    }),
  // ===== Acesso à extensão (gate de geração de token) — [[project-chrome-extension-assistant]] =====
  apiTokenAccessGet: () =>
    call<{ access: ApiTokenAccess }>("/api/manager/api-tokens"),
  apiTokenAccessSet: (
    mode: ApiTokenAccess["mode"],
    allowlist: string[],
    extra?: { ttlHours?: number; maxDevices?: number }
  ) =>
    call<{ access: ApiTokenAccess }>("/api/manager/api-tokens", {
      method: "PATCH",
      body: JSON.stringify({ mode, allowlist, ...extra })
    }),
  // AI usage (token tracking)
  aiUsageSummary: (range: string = "30d", opts?: { accessMode?: string; feature?: string }) => {
    const qs = new URLSearchParams({ range });
    if (opts?.accessMode) qs.set("accessMode", opts.accessMode);
    if (opts?.feature) qs.set("feature", opts.feature);
    return call<AiUsageSummaryResponse>(`/api/manager/ai-usage?${qs.toString()}`);
  },
  aiUsageForUser: (userId: string, range: string = "30d") =>
    call<AiUsageSummaryResponse>(
      `/api/manager/ai-usage?userId=${encodeURIComponent(userId)}&range=${range}`
    ),
  aiCostTable: () => call<{ ok: true; rows: AiCostTableRow[] }>(`/api/manager/ai-usage?costTable=1`),
  upsertAiCost: (row: {
    provider: string;
    model: string;
    inputPer1kTenthCents: number;
    outputPer1kTenthCents: number;
  }) =>
    call<{ ok: true }>(`/api/manager/ai-usage?action=upsertCost`, {
      method: "POST",
      body: JSON.stringify(row)
    }),

  // ===== Toggle de alertas admin (sem deploy) — [[project-admin-alerts-toggle]] =====
  getAdminAlertsConfig: () =>
    call<{
      enabled: boolean;
      note: string | null;
      updatedAt: string | null;
      updatedBy: string | null;
      updatedByName: string | null;
      updatedByEmail: string | null;
      recent: Array<{
        key: string;
        source: string;
        summary: string;
        severity: "warning" | "critical";
        totalHits: number;
        lastSentAt: string;
      }>;
    }>("/api/manager/admin-alerts-config"),
  setAdminAlertsConfig: (data: { enabled: boolean; note?: string }) =>
    call<{ ok: true; enabled: boolean }>("/api/manager/admin-alerts-config", {
      method: "PATCH",
      body: JSON.stringify(data)
    }),

  // ===== Sistema de créditos (planos + faixas + custos) — [[project-credits-system]] =====
  listCreditPlans: (opts?: { includeDeleted?: boolean }) =>
    call<{ plans: CreditPlanRow[] }>(
      `/api/manager/credit-plans${opts?.includeDeleted ? "?includeDeleted=1" : ""}`
    ),
  createCreditPlan: (data: Partial<CreditPlanRow>) =>
    call<{ ok: true; plan: CreditPlanRow }>("/api/manager/credit-plans", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  updateCreditPlan: (planId: string, data: Partial<CreditPlanRow>) =>
    call<{ ok: true; plan: CreditPlanRow }>(
      `/api/manager/credit-plans?planId=${encodeURIComponent(planId)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteCreditPlan: (planId: string) =>
    call<{ ok: true }>(
      `/api/manager/credit-plans?planId=${encodeURIComponent(planId)}`,
      { method: "DELETE" }
    ),
  archiveCreditPlan: (planId: string, unarchive = false) =>
    call<{ ok: true }>(
      `/api/manager/credit-plans?planId=${encodeURIComponent(planId)}&action=${unarchive ? "unarchive" : "archive"}`,
      { method: "POST", body: "{}" }
    ),
  listCreditPlanAllowlist: (planId: string) =>
    call<{ allowlist: CreditPlanAllowlistEntry[] }>(
      `/api/manager/credit-plans?planId=${encodeURIComponent(planId)}&view=allowlist`
    ),
  grantCreditPlanAllowlist: (planId: string, userId: string, notes?: string) =>
    call<{ ok: true }>(
      `/api/manager/credit-plans?planId=${encodeURIComponent(planId)}&action=grant`,
      { method: "POST", body: JSON.stringify({ userId, notes }) }
    ),
  revokeCreditPlanAllowlist: (planId: string, grantId: string) =>
    call<{ ok: true }>(
      `/api/manager/credit-plans?planId=${encodeURIComponent(planId)}&grantId=${encodeURIComponent(grantId)}`,
      { method: "DELETE" }
    ),

  listCreditTiers: () =>
    call<{ tiers: CreditTierRow[] }>("/api/manager/credit-tiers"),
  createCreditTier: (data: Partial<CreditTierRow>) =>
    call<{ ok: true; tier: CreditTierRow }>("/api/manager/credit-tiers", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  updateCreditTier: (tierId: string, data: Partial<CreditTierRow>) =>
    call<{ ok: true; tier: CreditTierRow }>(
      `/api/manager/credit-tiers?tierId=${encodeURIComponent(tierId)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteCreditTier: (tierId: string) =>
    call<{ ok: true }>(
      `/api/manager/credit-tiers?tierId=${encodeURIComponent(tierId)}`,
      { method: "DELETE" }
    ),

  listFeatureCosts: () =>
    call<{ costs: FeatureCostRow[] }>("/api/manager/feature-costs"),
  createFeatureCost: (data: Partial<FeatureCostRow>) =>
    call<{ ok: true; cost: FeatureCostRow }>("/api/manager/feature-costs", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  updateFeatureCost: (costId: string, data: Partial<FeatureCostRow>) =>
    call<{ ok: true; cost: FeatureCostRow }>(
      `/api/manager/feature-costs?costId=${encodeURIComponent(costId)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteFeatureCost: (costId: string) =>
    call<{ ok: true }>(
      `/api/manager/feature-costs?costId=${encodeURIComponent(costId)}`,
      { method: "DELETE" }
    ),

  // ===== Créditos (pré-pago manual) — [[project-prepaid-credits-strategy]] =====
  listUserCredits: (q?: string) =>
    call<{
      items: Array<{
        userId: string;
        email: string | null;
        name: string | null;
        balanceCents: number;
        totalToppedUpCents: number;
        totalConsumedCents: number;
        updatedAt: string;
      }>;
    }>(`/api/manager/user-credits${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  getUserCredits: (userId: string) =>
    call<{
      user: { id: string; email: string; name: string };
      credits: {
        balanceCents: number;
        totalToppedUpCents: number;
        totalConsumedCents: number;
        updatedAt: string;
      };
      ledger: Array<{
        id: string;
        deltaCents: number;
        kind: string;
        description: string | null;
        reference: unknown;
        createdAt: string;
      }>;
    }>(`/api/manager/user-credits?userId=${encodeURIComponent(userId)}`),
  grantUserCredits: (
    userId: string,
    data: { amountCents: number; description: string; kind?: "purchase" | "bonus" | "adjust" | "refund" }
  ) =>
    call<{
      ok: true;
      balanceCents: number;
      ledgerEntry: { id: string; deltaCents: number; kind: string; description: string | null; createdAt: string };
    }>(`/api/manager/user-credits?userId=${encodeURIComponent(userId)}`, {
      method: "POST",
      body: JSON.stringify(data)
    }),
  adjustUserCredits: (userId: string, data: { amountCents: number; description: string }) =>
    call<{
      ok: true;
      balanceCents: number;
      ledgerEntry: { id: string; deltaCents: number; kind: string; description: string | null; createdAt: string };
    }>(`/api/manager/user-credits?userId=${encodeURIComponent(userId)}&action=adjust`, {
      method: "POST",
      body: JSON.stringify(data)
    }),

  // ============================================
  // Biblioteca de Temas de Redação (admin)
  // [[project-essay-themes-library]]
  // ============================================
  listEssayThemes: () =>
    call<{ themes: EssayThemeRow[] }>("/api/manager/essay-themes"),
  getEssayTheme: (id: string) =>
    call<EssayThemeFull>(`/api/manager/essay-themes?id=${encodeURIComponent(id)}`),
  upsertEssayTheme: (theme: EssayThemeUpsertInput) =>
    call<{ ok: true; theme: EssayThemeFull }>("/api/manager/essay-themes", {
      method: "POST",
      body: JSON.stringify(theme)
    }),
  patchEssayTheme: (id: string, patch: Partial<EssayThemeUpsertInput>) =>
    call<{ ok: true; theme: EssayThemeFull }>(
      `/api/manager/essay-themes?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(patch) }
    ),
  deleteEssayTheme: (id: string) =>
    call<{ ok: true }>(`/api/manager/essay-themes?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  ingestEssayThemeJson: (payload: EssayThemeUpsertInput) =>
    call<{ ok: true; theme: EssayThemeFull }>("/api/manager/essay-themes-ingest", {
      method: "POST",
      body: JSON.stringify({ mode: "json", payload })
    })
};

export interface EssayThemeRow {
  id: string;
  slug: string;
  title: string;
  displayName: string;
  banca: string;
  year: number | null;
  phase: string | null;
  edition: string | null;
  applicationDate: string | null;
  examEditionKey: string | null;
  category: string;
  difficulty: string;
  visibility: "private" | "unlisted" | "public";
  status: "draft" | "published" | "archived";
  originalSourceUrl: string | null;
  /** Pasta da Biblioteca (compartilhada com banks). null = sem pasta. */
  libraryFolderId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EssayThemeItem {
  id?: string;
  kind: "text" | "image" | "table";
  content: string;
  imageDataUrl: string | null;
  tableData: { headers?: string[]; rows?: string[][]; caption?: string } | null;
  source: string;
  sourceKind: "real" | "ai_generated" | "unknown";
  position: number;
}

export interface EssayThemeFragment {
  id?: string;
  label: string;
  sourceOverall: string;
  note: string;
  position: number;
  items: EssayThemeItem[];
}

export interface EssayThemeFull extends EssayThemeRow {
  statement: string;
  officialInstructions: string;
  directives: string[];
  fragments: EssayThemeFragment[];
}

export interface EssayThemeUpsertInput {
  id?: string;
  slug?: string;
  title: string;
  displayName?: string;
  banca?: string;
  year?: number | null;
  phase?: string | null;
  edition?: string | null;
  applicationDate?: string | null;
  examEditionKey?: string | null;
  category?: string;
  difficulty?: string;
  statement?: string;
  officialInstructions?: string;
  directives?: string[];
  originalSourceUrl?: string | null;
  visibility?: "private" | "unlisted" | "public";
  status?: "draft" | "published" | "archived";
  libraryFolderId?: string | null;
  fragments?: EssayThemeFragment[];
}

export interface AiUsageOverall {
  events: number;
  okEvents: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  costPlatformCents: number;
  costPlatformBillableCents: number;
  costPlatformFailedCents: number;
  costPlatformNano: number;
  costPlatformBillableNano: number;
  costPlatformFailedNano: number;
  tokensPlatform: number;
  tokensByok: number;
  uniqueUsers: number;
}

export interface AiUsageTopUser {
  userId: string;
  userName: string;
  userEmail: string;
  events: number;
  tokens: number;
  tokensPlatform: number;
  tokensByok: number;
  costPlatformCents: number;
  costPlatformNano: number;
}

export interface AiUsageByFeature {
  feature: string;
  events: number;
  tokens: number;
  tokensPlatform: number;
  tokensByok: number;
  costPlatformCents: number;
  costPlatformNano: number;
}

export interface AiUsageByProvider {
  provider: string;
  model: string;
  events: number;
  tokens: number;
  costPlatformCents: number;
  costPlatformNano: number;
}

export interface AiUsageEventRow {
  id: string;
  feature: string;
  provider: string;
  model: string;
  accessMode: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsdCents: number | null;
  costNanoUsd: number | null;
  requestMs: number;
  ok: boolean;
  fallbackUsed: boolean;
  errorCode: string | null;
  createdAt: string;
}

export interface AiUsageSummaryResponse {
  ok: true;
  range: { days: number; since: string };
  overall: AiUsageOverall | null;
  topUsers: AiUsageTopUser[];
  byFeature: AiUsageByFeature[];
  byProvider: AiUsageByProvider[];
  recentEvents: AiUsageEventRow[];
}

export interface AiCostTableRow {
  id: string;
  provider: string;
  model: string;
  inputPer1kTenthCents: number;
  outputPer1kTenthCents: number;
  updatedAt: string;
}

export interface BrandingConfig {
  platformName: string;
  faviconUrl: string | null;
  logoUrl: string | null;
  primaryColor: string;
  portalLabels: {
    student: string;
    teacher: string;
    company: string;
    manager: string;
    auth: string;
  };
  loginSubtitle: string | null;
  footerText: string | null;
  updatedAt: number | null;
  updatedBy: string | null;
}

export interface LegalDocument {
  id: string;
  kind: "terms" | "privacy" | "cookies";
  version: number;
  title: string;
  content: string;
  summary: string | null;
  status: "draft" | "published" | "archived";
  contentHash: string | null;
  effectiveAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LegalRollout {
  mode: "off" | "allowlist" | "all";
  allowlist: string[];
}

export interface ApiTokenAccess {
  mode: "off" | "allowlist" | "all";
  allowlist: string[];
  ttlHours: number;
  maxDevices: number;
}

export interface AccountClosureRow {
  id: string;
  userId: string;
  status: "requested" | "processed" | "cancelled";
  reason: string | null;
  email: string | null;
  name: string | null;
  suspended: boolean;
  requestedAt: string;
  processedAt: string | null;
}

export interface ErrorLogRow {
  id: string;
  source: string;
  message: string;
  stack: string | null;
  path: string | null;
  userEmail: string | null;
  userAgent: string | null;
  release: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

export interface NotificationTypeRow {
  key: string;
  label: string;
  description: string | null;
  channels: string[];
  triggerDescription: string | null;
  examplePayload: Record<string, unknown> | null;
  isEnabled: boolean;
  updatedAt: string;
}

export interface NotificationHistoryRow {
  id: string;
  userId: string;
  title: string;
  body: string | null;
  payload: Record<string, unknown> | null;
  linkUrl: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface AiFeatureConfigRow {
  featureKey: string;
  provider: string;
  model: string;
  notes: string | null;
  updatedAt: string;
  apiKeySet: boolean;
}

export interface PlatformSubscriptionCharge {
  id: string;
  status: "pending" | "succeeded" | "failed" | "refunded";
  provider: "pix_manual" | "stripe" | "mercadopago" | "celcoin" | "mock";
  valorCents: number;
  failureReason: string | null;
  methodPriority: number | null;
  chargedAt: string;
}

export interface PlatformSubscriptionRow {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  offeringId: string | null;
  companyId: string | null;
  companyName: string | null;
  pacoteNomeSnap: string;
  valorCents: number;
  billingType: "pix_monthly" | "credit_card";
  status: "active" | "paused" | "past_due" | "canceled";
  nextChargeAt: string;
  lastChargeAt: string | null;
  failedAttempts: number;
  createdAt: string;
  scope: "platform" | "company";
  recentCharges: PlatformSubscriptionCharge[];
}

export interface PlatformPlanRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  monthlyCents: number;
  features: Record<string, unknown>;
  isActive: boolean;
  isPublic: boolean;
  isDefault: boolean;
  displayOrder: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Quantos usuários têm sub ativa nesse plano (vem do server). */
  memberCount?: number;
}

/** Plano do sistema de créditos (migration 0049). [[project-credits-system]] */
export interface CreditPlanRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  monthlyCents: number;
  features: Record<string, unknown>;
  monthlyCostCredits: number;
  depositMultiplier: number;
  consumptionMultiplier: number;
  monthlyGrantedCredits: number;
  allowNegativeBalance: boolean;
  negativeBalanceFloor: number;
  visibility: "public" | "allowlist" | "hidden";
  isActive: boolean;
  isPublic: boolean;
  isDefault: boolean;
  displayOrder: number;
  archivedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreditPlanAllowlistEntry {
  id: string;
  userId: string;
  userEmail: string;
  userName: string | null;
  grantedAt: string;
  notes: string | null;
}

export interface CreditTierRow {
  id: string;
  minBrlCents: number;
  bonusPercent: number;
  displayOrder: number;
  isActive: boolean;
  updatedAt: string;
}

export interface FeatureCostRow {
  id: string;
  featureKey: string;
  costCredits: number;
  category: string;
  description: string | null;
  isActive: boolean;
  updatedAt: string;
}

export interface EmailTemplateVariable {
  key: string;
  description: string;
  example: string;
}

export interface EmailTemplateRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  subject: string;
  html: string;
  textBody: string;
  variables: EmailTemplateVariable[];
  isActive: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export interface EmailConfigRow {
  id: string;
  resendApiKeyMasked: string;
  resendApiKeySet: boolean;
  resendFrom: string;
  resendDefaultDomain: string;
  updatedAt: string;
}

export interface EmailRuntimeStatus {
  configured: boolean;
  source: "db" | "env" | "none";
  effectiveFrom: string;
  envKeyConfigured: boolean;
}

export type FeatureFlagAudience = "all" | "allowlist" | "off";
export type FeatureFlagPlatform =
  | "any"
  | "platform"
  | "company"
  | "teacher"
  | "student";
export type FeatureFlagStatus =
  | "experimental"
  | "beta"
  | "stable"
  | "deprecated";

export interface FeatureFlagRow {
  id: string;
  key: string;
  label: string;
  description: string;
  platform: FeatureFlagPlatform;
  audience: FeatureFlagAudience;
  status: FeatureFlagStatus;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
  userCount?: number;
  groupCount?: number;
}

export interface PlanMemberRow {
  subscriptionId: string;
  userId: string;
  userName: string;
  userEmail: string;
  status: "active" | "paused" | "past_due" | "canceled";
  billingType: "pix_monthly" | "credit_card";
  valorCents: number;
  nextChargeAt: string;
  lastChargeAt: string | null;
  failedAttempts: number;
  createdAt: string;
}

export interface OpenChargeRow {
  id: string;
  subscriptionId: string;
  status: "pending" | "failed";
  valorCents: number;
  provider: "pix_manual" | "stripe" | "mercadopago" | "celcoin" | "mock";
  methodPriority: number | null;
  failureReason: string | null;
  pixBrcode: string | null;
  pixTxid: string | null;
  chargedAt: string;
  confirmedAt: string | null;
  userName: string;
  userEmail: string;
  pacoteNomeSnap: string;
  subStatus: "active" | "paused" | "past_due" | "canceled";
  platformPlanSlug: string | null;
}

export interface PlatformSubscriptionSummary {
  active: number;
  pastDue: number;
  paused: number;
  totalMonthly: number;
  byType: { pix_monthly: number; credit_card: number };
  byScope: { platform: number; company: number };
}

export interface SignupAllowlistRow {
  id: string;
  email: string;
  emailNormalized: string;
  status: "pending" | "used" | "revoked";
  targetCompanyId: string | null;
  targetCompanyName: string | null;
  targetRole: "admin_company" | "teacher" | "student" | null;
  targetGroupIds: string[];
  note: string | null;
  invitedAt: string;
  usedAt: string | null;
  revokedAt: string | null;
}

export interface PaymentsConfig {
  id: string;
  pixEnabled: boolean;
  platformPixKey: string;
  platformPixRecipientName: string;
  platformPixCity: string;
  platformPixMode: "static" | "dynamic";
  ccEnabled: boolean;
  ccProvider: "pix_manual" | "stripe" | "mercadopago" | "celcoin" | "mock";
  ccConfig: Record<string, unknown>;
  mockAlwaysSucceeds: boolean;
  pixProvider: "manual" | "celcoin";
  updatedAt: string;
}

export interface AiStudyAccessRow {
  userId: string;
  email: string;
  name: string;
  grantedAt: string;
  revokedAt: string | null;
  grantedBy: string | null;
  notes: string;
  active: boolean;
}
